import { FinSightLogo } from "./FinSightLogo";
import { PortfolioUploadStep } from "./PortfolioUploadStep";
import { ManualFeeForm } from "./ManualFeeForm";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import {
  TrendingUp,
  BarChart3,
  Users,
  ArrowRight,
  Check,
  Sparkles,
  Upload,
  FileSpreadsheet,
  Plus,
  X,
  AlertCircle,
  DollarSign,
  Percent,
  Edit,
  Trash2,
  LogOut,
  CalendarIcon,
} from "lucide-react";
import { PortfolioPosition } from "../interface/PortfolioPosition";
import { PortfolioAccount } from "../interface/PortfolioAccount";
import { FeeStructureOption, FeeType } from "../interface/profile/FeeStructureOption";
import { OnboardingData } from "../interface/OnboardingData";
import { OnboardingFlowProps } from "../interface/OnboardingFlowProps";
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import useOnboardingProgress from "@/hooks/useOnboardingProgress";
import { useAuth } from '../hooks/useAuth';
import { UpdateProfileRequest } from "../interface/UpdateProfileRequest";
import { Footer } from "./Footer";
// Import standardized taxonomy for consistent matching
import {
  FINANCIAL_SPECIALIZATIONS,
  SECTORS,
  RESEARCH_STYLES,
  RISK_TOLERANCE_LEVELS,
  ADVISING_REQUEST_TYPES,
  CERTIFICATIONS,
  ADVISOR_FEE_TYPES,
  ANALYST_FEE_TYPES,
  INVESTMENT_EXPERIENCE_LEVELS,
  AdvisorFeeType,
  AnalystFeeType,
  getAdvisorFeeTypeDetails,
  getAnalystFeeTypeDetails,
} from "../utils/financialTaxonomy";
//type Role = "investor" | "analyst" | "advisor" | null;

export function OnboardingFlow({ onComplete, onLogout }: OnboardingFlowProps) {
  //const [step, setStep] = useState(0);
  const [step, setStep] = useState(() => {
    const savedStep = localStorage.getItem('currentStep');
    return savedStep ? parseInt(savedStep, 10) : 0;
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [data, setData] = useState<OnboardingData>({
    role: null,
    fullName: "",
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
    address: {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    occupation: {
      occupationType: "",
      company: "",
      jobTitle: "",
      details: "",
    },
    portfolioAccounts: [],
    feeStructureOptions: [],
    currentStep: 0,
    acceptingNewClients: false,
    freeConsultation: false,
    consultationDuration: 0,
    completed: false,
    completedAt: null,
    linkedinUrl: "",
    
  });
  /* const [data, setData] = useState<UpdateProfileRequest>({
    role: "",
    fullName: "",
    email: "",
    onboardingCurrentStep: 0,
    onboardingCompleted: false,
    onboardingCurrentStepName: "",
    onboardingCompletedAtFormatted: "",
  }); */
  const navigate = useNavigate();
  
  // State for manual fee structure form (must be at top level, not conditional)
  const [showManualForm, setShowManualForm] = useState(false);
  const [editingFeeId, setEditingFeeId] = useState<string | null>(null);
  const [manualFee, setManualFee] = useState<Partial<FeeStructureOption>>({
    feeType: undefined,
    name: "",
  });

  // Onboarding progress tracking
  const { saveProgress, markCompleted } = useOnboardingProgress();
  const { refreshToken } = useAuth();``
  const userId = localStorage.getItem('userId'); // Get from
  const authStatus = localStorage.getItem('isAuthenticated'); 
  //const currentStep = localStorage.getItem('currentStep')? parseInt(localStorage.getItem('currentStep')) : 0;
  //setStep(currentStep);
  if (!userId || authStatus !== 'true') {
    console.log('User is not authenticated, redirecting to login');
    localStorage.setItem('isAuthenticated', 'false');
    //navigate('/login'); // or the path to your login page
    window.location.href = '/login';
    return;
  }

  const getToken = () => {
    return localStorage.getItem('authToken');
  };


  /**
   * Check JWT token expiration and refresh if needed
   * Also check onboarding completion status
   */
  useEffect(() => {
    const checkAuthAndOnboarding = async () => {
      try {
        const token = getToken();
        console.log('Token:', token);
        // Check if token is expired
        if (!token) {
          console.log('Token is expired or missing, redirecting to login');
          if (onLogout) {
            onLogout();
          }
          window.location.href = '/login';
          return;
        }

        // Token is valid, refresh and get onboarding status
        try {
          const refreshResponse = await refreshToken(token);
          console.log('Refresh response:', refreshResponse);
          if(refreshResponse != null){
            data.email = refreshResponse.email;
            data.role = refreshResponse.role;
            data.completed = refreshResponse.onboardingCompleted;
            data.currentStep = refreshResponse.currentStep;
            data.fullName = refreshResponse.fullName
            data.firstName = (refreshResponse.firstName == null || refreshResponse.firstName == undefined)
              ? refreshResponse.fullName.split(' ')[0]
              : refreshResponse.firstName
            data.lastName = (refreshResponse.lastName == null || refreshResponse.lastName == undefined)
              ? refreshResponse.fullName.split(' ')[1]
              : refreshResponse.lastName
          }
          
          // Check if onboarding is completed
          if (refreshResponse.onboardingCompleted === true) {
            // Redirect to dashboard
            console.log('Onboarding already completed, redirecting to dashboard');
            //navigate('/dashboard'); // or use router navigation
            onComplete(data, data.role);
            //navigate('/dashboard');
            window.location.href = '/dashboard';
            return;
          }

          // If not completed, check current step and update
          if (refreshResponse.currentStep !== undefined && refreshResponse.currentStep !== null) {
            localStorage.setItem('currentStep', refreshResponse.currentStep.toString());
            setStep(refreshResponse.currentStep);
          }

          // Update role if available
          if (refreshResponse.role) {
            setData(prev => ({ ...prev, role: refreshResponse.role.toLowerCase()}));
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // If refresh fails, logout
          if (onLogout) {
            onLogout();
          }
          window.location.href = '/login';
          return;
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (onLogout) {
          onLogout();
        }
        window.location.href = '/login';
        return;
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthAndOnboarding();
  }, []); // Run once on mount

  /* useEffect(() => {
    localStorage.setItem('currentStep', step.toString());
  }, [step]);
 */
  const updateData =  (updates: Partial<UpdateProfileRequest>) => {
    //setData((prev) => ({ ...prev, ...updates }));
    //setData(updates);
    setData(prev => {
    console.log('Previous state:', prev);
    console.log('Updates:', updates);  // Check what's in updates
    const newState = { ...prev, ...updates };
    setStep(newState.onboardingCurrentStep);
    console.log('New state:', newState);  // Verify the merged state
    if (userId && newState.onboardingCurrentStep>0) {
       saveProgress(userId, newState);
    }
    return newState;
  });
  };

  const updateDataNew =  (updates: Partial<OnboardingData>) => {
    //setData((prev) => ({ ...prev, ...updates }));
    //setData(updates);
    setData(prev => {
    console.log('Previous state:', prev);
    console.log('Updates:', updates);  // Check what's in updates
    const newState = { ...prev, ...updates };
    if(step == 0){
      console.log('New state:', newState);  // Verify the merged state
      if (userId && newState.currentStep>0) {
        saveProgress(userId, newState);
      }
      setStep(newState.currentStep);
    }
    return newState;
  });
  };



  /**
   * Navigate to next step and save progress to backend
   */
  const goToNextStep = async (onboardingCurrentStep: number) => {
    //setStep(nextStep);
    //setData((prev) => ({ ...prev, currentStep: onboardingCurrentStep }));
    //setData({...data, currentStep: onboardingCurrentStep});
    //setData(onboardingCurrentStep);
    // Create the updated data object first
    const updatedData = {
      ...data,  // Spread existing data
      currentStep: onboardingCurrentStep  // Update currentStep
    };
  
  // Update the state
    setData(updatedData);
    // Save progress to backend
    if (userId && onboardingCurrentStep>0) {
      await saveProgress(userId, updatedData);
    }
    setStep(onboardingCurrentStep);
  };

  const handleComplete = () => {
    data.completed = true;
    //saveProgress(userId, data);
    markCompleted(userId);
    onComplete(data, data.role);
  };

  /**
   * Handle logout - clear data and call onLogout callback
   */
  const handleLogout = () => {
    // Clear local storage
    localStorage.clear();
    if (onLogout) {
      onLogout();
    }
    //navigate('/login');
    window.location.href = '/login';
  };

  const getTotalSteps = () => {
    if (!data.role) return 6;
    if (data.role?.toLowerCase() === "investor") return 6; // Role + Basic + Profile + Details + Portfolio Upload + Completion
    return 6; // Role + Basic + 2 role-specific + Fee Structure + Completion
  };

  const progressPercentage = ((step + 1) / getTotalSteps()) * 100;

  // Logout Button Component
  const LogoutButton = () => (
    <div className="absolute top-4 right-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLogout}
        className="text-muted-foreground hover:text-destructive"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Logout
      </Button>
    </div>
  );

  // Function to handle saving account data
  const handleSaveAccount = async (accountData: any) => {
    try {
      const updatedAccounts = [...(data.portfolioAccounts || []), accountData];
      const updatedData = { 
        ...data, 
        portfolioAccounts: updatedAccounts,
        currentStep: 4 // Update to the next step if needed
      };
      
      // Save the updated data
      await saveProgress(userId, updatedData);
      
      // Update local state
      setData(updatedData);
      return true;
    } catch (error) {
      console.error('Failed to save account:', error);
      return false;
    }
  };

  const handleUpdateAccount = async (updatedAccount: any, index: number) => {
    try {
      const updatedAccounts = [...(data.portfolioAccounts || [])];
      updatedAccounts[index] = updatedAccount;
      
      const updatedData = { 
        ...data, 
        portfolioAccounts: updatedAccounts 
      };
      
      await saveProgress(userId, updatedData);
      setData(updatedData);
      return true;
    } catch (error) {
      console.error('Failed to update account:', error);
      return false;
    }
  };

  const handleDeleteAccount = async (index: number) => {
    try {
      const updatedAccounts = [...(data.portfolioAccounts || [])];
      updatedAccounts.splice(index, 1);
      
      const updatedData = { 
        ...data, 
        portfolioAccounts: updatedAccounts 
      };
      
      await saveProgress(userId, updatedData);
      setData(updatedData);
      return true;
    } catch (error) {
      console.error('Failed to delete account:', error);
      return false;
    }
};

  // Step 0: Role Selection
  if (step === 0) {
    return (
       <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-4xl w-full">
          <LogoutButton />
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto mb-4">
              <FinSightLogo variant="icon" size="lg" />
            </div>
            <CardTitle className="text-3xl">Welcome to Financial Insights Hub</CardTitle>
            <CardDescription>
              Let's get started by selecting your role in the platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => {
                  //updateData({ role: "investor",onboardingCurrentStep:1 });
                  updateDataNew({ role: "investor",currentStep:1});
                  //goToNextStep(1);
                }}
                className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-all p-6 text-left bg-card hover:shadow-lg"
              >
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="mb-2">Investor</h3>
                    <p className="text-sm text-muted-foreground">
                      Manage your portfolio, track investments, and connect with
                      advisors
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  //updateData({ role: "analyst",onboardingCurrentStep:1 });
                  updateDataNew({ role: "analyst",currentStep:1 });
                  //goToNextStep(1);
                }}
                className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-all p-6 text-left bg-card hover:shadow-lg"
              >
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <BarChart3 className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="mb-2">Financial Analyst</h3>
                    <p className="text-sm text-muted-foreground">
                      Provide research, insights, and market analysis to clients
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  //updateData({ role: "advisor",onboardingCurrentStep:1 });
                  updateDataNew({ role: "advisor",currentStep:1 });
                  //goToNextStep(1);
                }}
                className="group relative overflow-hidden rounded-lg border-2 border-border hover:border-primary transition-all p-6 text-left bg-card hover:shadow-lg"
              >
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="mb-2">Financial Advisor</h3>
                    <p className="text-sm text-muted-foreground">
                      Guide clients with personalized financial advice and planning
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>
         </div>
        <Footer />
      </div>
    );
  }

  // Step 1: Basic Information
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <LogoutButton />
          <CardHeader>
            <Progress value={progressPercentage} className="mb-4" />
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Tell us a bit about yourself to personalize your experience
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={data.firstName || ""}
                  onChange={(e) => updateDataNew({ firstName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={data.lastName || ""}
                  onChange={(e) => updateDataNew({ lastName: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="john.doe@example.com"
                value={data.email}
                onChange={(e) => updateDataNew({ email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number *</Label>
              <Input
                id="phoneNumber"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={data.phoneNumber || ""}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                  let formatted = '';
                  if (!digits) {
                    formatted = '';
                  } else if (digits.length <= 1) {
                    formatted = `+${digits}`;
                  } else if (digits.length <= 4) {
                    formatted = `+${digits[0]} (${digits.slice(1)}`;
                  } else if (digits.length <= 7) {
                    formatted = `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4)}`;
                  } else {
                    formatted = `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
                  }
                  updateDataNew({ phoneNumber: formatted });
                }}
              />
            </div>

            <div className="space-y-2">
                <Label htmlFor="dateOfBirth">
                  Date of Birth
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="dateOfBirth"
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={
                      data.dateOfBirth
                        ? typeof data.dateOfBirth === "string"
                          ? data.dateOfBirth
                          : data.dateOfBirth.toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                        : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      // Auto-format as user types: MM/DD/YYYY
                      let formatted = value.replace(/\D/g, ""); // Remove non-digits
                      
                      if (formatted.length >= 2) {
                        formatted = formatted.slice(0, 2) + "/" + formatted.slice(2);
                      }
                      if (formatted.length >= 5) {
                        formatted = formatted.slice(0, 5) + "/" + formatted.slice(5);
                      }
                      if (formatted.length > 10) {
                        formatted = formatted.slice(0, 10);
                      }
                      
                      updateDataNew({ dateOfBirth: e.target.value });
                      
                      // Try to parse complete date (MM/DD/YYYY format)
                      if (formatted.length === 10) {
                        const parts = formatted.split("/");
                        const month = parseInt(parts[0], 10);
                        const day = parseInt(parts[1], 10);
                        const year = parseInt(parts[2], 10);
                        
                        // Validate date components
                        if (
                          month >= 1 &&
                          month <= 12 &&
                          day >= 1 &&
                          day <= 31 &&
                          year >= 1900 &&
                          year <= 2100
                        ) {
                          const date = new Date(year, month - 1, day);
                          // Verify the date is valid (handles invalid dates like Feb 31)
                          if (
                            date.getMonth() === month - 1 &&
                            date.getDate() === day
                          ) {
                            updateDataNew({ dateOfBirth: e.target.value });
                          }
                        }
                      }
                    }}
                    maxLength={10}
                    className="flex-1"
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                      >
                        <CalendarIcon className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          data.dateOfBirth
                            ? typeof data.dateOfBirth === "string"
                              ? (() => {
                                  // Parse MM/DD/YYYY format
                                  const parts = data.dateOfBirth.split("/");
                                  if (parts.length === 3) {
                                    const month = parseInt(parts[0], 10);
                                    const day = parseInt(parts[1], 10);
                                    const year = parseInt(parts[2], 10);
                                    if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
                                      return new Date(year, month - 1, day);
                                    }
                                  }
                                  return undefined;
                                })()
                              : data.dateOfBirth
                            : undefined
                        }
                        onSelect={(date) => {
                          if (date) {
                            const month = String(date.getMonth() + 1).padStart(2, "0");
                            const day = String(date.getDate()).padStart(2, "0");
                            const year = date.getFullYear();
                            updateDataNew({ dateOfBirth: `${month}/${day}/${year}` });
                          }
                        }}
                        defaultMonth={
                          data.dateOfBirth
                            ? typeof data.dateOfBirth === "string"
                              ? (() => {
                                  const parts = data.dateOfBirth.split("/");
                                  if (parts.length === 3) {
                                    const year = parseInt(parts[2], 10);
                                    const month = parseInt(parts[0], 10);
                                    if (!isNaN(year) && !isNaN(month)) {
                                      return new Date(year, month - 1);
                                    }
                                  }
                                  return new Date(1990, 0);
                                })()
                              : data.dateOfBirth
                            : new Date(1990, 0)
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

            {/* Occupation Section */}
            <div className="space-y-4 pt-2">
              <h4 className="font-medium">Occupation</h4>
              
              <div className="space-y-2">
                <Label htmlFor="occupationType">Occupation Type</Label>
                <Select
                  value={data.occupation?.occupationType}
                  onValueChange={(value) => updateDataNew({ occupation: { ...data.occupation, occupationType: value } })}
                >
                  <SelectTrigger id="occupationType">
                    <SelectValue placeholder="Select occupation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self-employed">Self Employed</SelectItem>
                    <SelectItem value="employed">Employed</SelectItem>
                    <SelectItem value="business-owner">Business Owner</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Conditionally show Company and Job Title for Employed or Business Owner */}
              {(data.occupation?.occupationType === "employed" || data.occupation?.occupationType === "business-owner") && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      placeholder="Acme Corp"
                      value={data.occupation?.company || ""}
                      onChange={(e) => updateDataNew({ occupation: { ...data.occupation, company: e.target.value } })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jobTitle">Job Title</Label>
                    <Input
                      id="jobTitle"
                      placeholder="Investment Manager"
                      value={data.occupation?.jobTitle || ""}
                      onChange={(e) => updateDataNew({ occupation: { ...data.occupation, jobTitle: e.target.value } })}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="occupationDetails">Details</Label>
                <Textarea
                  id="occupationDetails"
                  placeholder="Additional information about your occupation..."
                  value={data.occupation?.details|| ""}
                  onChange={(e) => updateDataNew({ occupation: { ...data.occupation, details: e.target.value } })}
                  rows={3}
                />
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h4 className="font-medium">Address</h4>
              
              <div className="space-y-2">
                <Label htmlFor="addressLine1">Address Line 1</Label>
                <Input
                  id="addressLine1"
                  placeholder="123 Main Street"
                  value={data.address?.addressLine1 || ""}
                  onChange={(e) => updateDataNew({ address: { ...data.address, addressLine1: e.target.value } })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="addressLine2">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  placeholder="Apt 4B"
                  value={data.address?.addressLine2 || ""}
                  onChange={(e) => updateDataNew({ address: { ...data.address, addressLine2: e.target.value } })}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="New York"
                    value={data.address?.city || ""}
                    onChange={(e) => updateDataNew({ address: { ...data.address, city: e.target.value } })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    placeholder="NY"
                    value={data.address?.state || ""}
                    onChange={(e) => updateDataNew({ address: { ...data.address, state: e.target.value } })}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zipCode">Zip Code</Label>
                  <Input
                    id="zipCode"
                    placeholder="10001"
                    value={data.address?.zipCode || ""}
                    onChange={(e) => updateDataNew({ address: { ...data.address, zipCode: e.target.value } })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    placeholder="United States"
                    value={data.address?.country || ""}
                    onChange={(e) => updateDataNew({ address: { ...data.address, country: e.target.value } })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedInUrl">LinkedIn URL</Label>
              <Input
                id="linkedInUrl"
                type="url"
                placeholder="https://www.linkedin.com/in/johndoe"
                value={data.linkedinUrl || ""}
                onChange={(e) => updateDataNew({ linkedinUrl: e.target.value })}
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button
                onClick={() => goToNextStep(2)}
                disabled={!data.firstName || !data.lastName || !data.email || !data.phoneNumber}
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Investor Steps
  if (data.role?.toLowerCase() === "investor") {
    if (step === 2) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-2xl w-full">
            <LogoutButton />
            <CardHeader>
              <Progress value={progressPercentage} className="mb-4" />
              <CardTitle>Investment Profile</CardTitle>
              <CardDescription>
                Help us understand your investment preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>What are your investment goals? (Select all that apply)</Label>
                <div className="space-y-2">
                  {FINANCIAL_SPECIALIZATIONS.map((goal) => (
                    <div key={goal} className="flex items-center space-x-2">
                      <Checkbox
                        id={goal}
                        checked={data.investmentGoals?.includes(goal)}
                        onCheckedChange={(checked) => {
                          const current = data.investmentGoals || [];
                          updateDataNew({
                            investmentGoals: checked
                              ? [...current, goal]
                              : current.filter((g) => g !== goal),
                          });
                        }}
                      />
                      <Label htmlFor={goal} className="cursor-pointer">
                        {goal}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Preferred Research Style (Select all that apply)</Label>
                <div className="space-y-2">
                  {RESEARCH_STYLES.map((style) => (
                    <div key={style.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={style.value}
                        checked={data.preferredResearchStyles?.includes(style.value)}
                        onCheckedChange={(checked) => {
                          const current = data.preferredResearchStyles || [];
                          updateDataNew({
                            preferredResearchStyles: checked
                              ? [...current, style.value]
                              : current.filter((g) => g !== style.value),
                          });
                        }}
                      />
                      <Label htmlFor={style.value} className="cursor-pointer">
                        {style.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Risk Tolerance</Label>
                <RadioGroup
                  value={data.riskTolerance}
                  onValueChange={(value) => updateDataNew({ riskTolerance: value })}
                >
                  {RISK_TOLERANCE_LEVELS.map((level) => (
                    <div key={level.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={level.value} id={level.value} />
                      <Label htmlFor={level.value} className="cursor-pointer">
                        {level.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => goToNextStep(1)}>
                  Back
                </Button>
                <Button
                  onClick={() => goToNextStep(3)}
                  disabled={
                    !data.investmentGoals?.length || !data.riskTolerance
                  }
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
          <Footer />
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <LogoutButton />
            <CardHeader>
              <Progress value={progressPercentage} className="mb-4" />
              <CardTitle>Investment Details</CardTitle>
              <CardDescription>
                A few more details to complete your profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="portfolioSize">Portfolio Size</Label>
                <Select
                  value={data.portfolioSize}
                  onValueChange={(value) => updateDataNew({ portfolioSize: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select portfolio size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-50k">Under $50,000</SelectItem>
                    <SelectItem value="50k-250k">$50,000 - $250,000</SelectItem>
                    <SelectItem value="250k-1m">$250,000 - $1,000,000</SelectItem>
                    <SelectItem value="1m-5m">$1,000,000 - $5,000,000</SelectItem>
                    <SelectItem value="over-5m">Over $5,000,000</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience">Investment Experience</Label>
                <Select
                  value={data.investmentExperience}
                  onValueChange={(value) => {
                    const selectedLevel = INVESTMENT_EXPERIENCE_LEVELS.find(level => level.key === value);
                    updateDataNew({ 
                      investmentExperience: value,
                      yearsExperience: selectedLevel?.value
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVESTMENT_EXPERIENCE_LEVELS.map((level) => (
                      <SelectItem key={level.key} value={level.key}>
                        {level.details}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Sectors of Interest (Select all that apply)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "Technology",
                    "Healthcare",
                    "Finance",
                    "Real Estate",
                    "Energy",
                    "Consumer Goods",
                  ].map((sector) => (
                    <div key={sector} className="flex items-center space-x-2">
                      <Checkbox
                        id={sector}
                        checked={data.sectors?.includes(sector)}
                        onCheckedChange={(checked) => {
                          const current = data.sectors || [];
                          updateDataNew({
                            sectors: checked
                              ? [...current, sector]
                              : current.filter((s) => s !== sector),
                          });
                        }}
                      />
                      <Label htmlFor={sector} className="cursor-pointer">
                        {sector}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button
                  onClick={() => goToNextStep(4)}
                  disabled={
                    !data.portfolioSize ||
                    !data.investmentExperience ||
                    !data.sectors?.length
                  }
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
          <Footer />
        </div>
      );
    }

    // Step 4: Portfolio Upload (Investor only)
    if (step === 4) {
      return (
        <PortfolioUploadStep
          progressPercentage={progressPercentage}
          portfolioAccounts={data.portfolioAccounts}
          onSaveAccount={handleSaveAccount}
          onUpdateAccount={handleUpdateAccount}
          onDeleteAccount={handleDeleteAccount}
          onUpdate={(accounts) => updateDataNew({ portfolioAccounts: accounts })}
          onBack={() => setStep(3)}
          onContinue={() => goToNextStep(5)}
          onLogout={handleLogout}
        />
      );
    }
  }

  // Analyst Steps
  if (data.role === "analyst") {
    if (step === 2) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <LogoutButton />
            <CardHeader>
              <Progress value={progressPercentage} className="mb-4" />
              <CardTitle>Analytical Expertise</CardTitle>
              <CardDescription>
                Tell us about your analytical specializations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Areas of Expertise (Select all that apply)</Label>
                <div className="space-y-2">
                  {RESEARCH_STYLES.map((style) => (
                    <div key={style.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={style.value}
                        checked={data.researchExpertise?.includes(style.value)}
                        onCheckedChange={(checked) => {
                          const current = data.researchExpertise || [];
                          updateDataNew({
                            researchExpertise: checked
                              ? [...current, style.value]
                              : current.filter((g) => g !== style.value),
                          });
                        }}
                      />
                      <Label htmlFor={style.value} className="cursor-pointer">
                        {style.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Investment Focus (Select all that apply)</Label>
                <div className="space-y-2">
                  {FINANCIAL_SPECIALIZATIONS.map((focus) => (
                    <div key={focus} className="flex items-center space-x-2">
                      <Checkbox
                        id={`focus-${focus}`}
                        checked={data.investmentFocus?.includes(focus)}
                        onCheckedChange={(checked) => {
                          const current = data.investmentFocus || [];
                          updateDataNew({
                            investmentFocus: checked
                              ? [...current, focus]
                              : current.filter((f) => f !== focus),
                          });
                        }}
                      />
                      <Label htmlFor={`focus-${focus}`} className="cursor-pointer">
                        {focus}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="yearsExperience">Years of Experience</Label>
                <Select
                  value={data.yearsExperience}
                  onValueChange={(value) => updateDataNew({ yearsExperience: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select years of experience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-2">0-2 years</SelectItem>
                    <SelectItem value="3-5">3-5 years</SelectItem>
                    <SelectItem value="6-10">6-10 years</SelectItem>
                    <SelectItem value="10+">10+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => goToNextStep(1)}>
                  Back
                </Button>
                <Button
                  onClick={() => goToNextStep(3)}
                  disabled={
                    !data.researchExpertise?.length || !data.yearsExperience
                  }
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
          <Footer />
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
             <Card className="max-w-2xl w-full">
            <LogoutButton />
            <CardHeader>
              <Progress value={progressPercentage} className="mb-4" />
              <CardTitle>Focus & Certifications</CardTitle>
              <CardDescription>
                Share your sector focus and professional credentials
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Focus Sectors (Select all that apply)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {SECTORS.map((sector) => (
                      <div
                        key={sector}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`sector-${sector}`}
                          checked={data.focusSectors?.includes(
                            sector,
                          )}
                          onCheckedChange={(checked) => {
                            const current =
                              data.focusSectors || [];
                            updateDataNew({
                              focusSectors: checked
                                ? [...current, sector]
                                : current.filter(
                                    (s) => s !== sector,
                                  ),
                            });
                          }}
                        />
                        <Label
                          htmlFor={`sector-${sector}`}
                          className="cursor-pointer"
                        >
                          {sector}
                        </Label>
                      </div>
                    ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Certifications (Select all that apply)</Label>
                <div className="space-y-2">
                  {CERTIFICATIONS.map((cert) => (
                      <div
                        key={cert.value}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`cert-${cert.value}`}
                          checked={data.certifications?.includes(
                            cert.value,
                          )}
                          onCheckedChange={(checked) => {
                            const current =
                              data.certifications || [];
                            updateDataNew({
                              certifications: checked
                                ? [...current, cert.value]
                                : current.filter(
                                    (c) => c !== cert.value,
                                  ),
                            });
                          }}
                        />
                        <Label
                          htmlFor={`cert-${cert.value}`}
                          className="cursor-pointer"
                        >
                          {cert.label}
                        </Label>
                      </div>
                    ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="researchApproach">
                  Research Approach (Optional)
                </Label>
                <Textarea
                  id="researchApproach"
                  placeholder="Describe your research methodology..."
                  value={data.researchApproach || ""}
                  onChange={(e) =>
                    updateDataNew({ researchApproach: e.target.value })
                  }
                  rows={3}
                />
              </div>

               <div className="space-y-2">
                  <Label>Preferred Client Risk Tolerance (Select all that apply)</Label>
                  <p className="text-sm text-muted-foreground">
                    What risk profiles do you prefer to work with?
                  </p>
                  <div className="space-y-2">
                    {RISK_TOLERANCE_LEVELS.map((level) => (
                      <div key={level.value} className="flex items-start space-x-2">
                        <Checkbox
                          id={`analyst-risk-${level.value}`}
                          checked={data.clientRiskTolerance?.includes(level.value)}
                          onCheckedChange={(checked) => {
                            const current = data.clientRiskTolerance || [];
                            updateDataNew({
                              clientRiskTolerance: checked
                                ? [...current, level.value]
                                : current.filter((r) => r !== level.value),
                            });
                          }}
                        />
                        <div className="flex flex-col">
                          <Label htmlFor={`advisor-risk-${level.value}`} className="cursor-pointer font-medium">
                            {level.label}
                          </Label>
                          <p className="text-sm text-muted-foreground">{level.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button onClick={() => goToNextStep(4)} disabled={!data.focusSectors?.length}>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
          <Footer />
        </div>
      );
    }
  }

  // Advisor Steps
  if (data.role === "advisor") {
    if (step === 2) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-2xl w-full">
            <LogoutButton />  
            <CardHeader>
              <Progress value={progressPercentage} className="mb-4" />
              <CardTitle>Professional Credentials</CardTitle>
              <CardDescription>
                Share your certifications and specializations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Certifications (Select all that apply)</Label>
                <div className="space-y-2">
                  {CERTIFICATIONS.map((cert) => (
                      <div
                        key={cert.value}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`advisor-cert-${cert.value}`}
                          checked={data.advisorCertifications?.includes(
                            cert.value,
                          )}
                          onCheckedChange={(checked) => {
                            const current =
                              data.advisorCertifications || [];
                            updateDataNew({
                              advisorCertifications: checked
                                ? [...current, cert.value]
                                : current.filter(
                                    (c) => c !== cert.value,
                                  ),
                            });
                          }}
                        />
                        <Label
                          htmlFor={`advisor-cert-${cert.value}`}
                          className="cursor-pointer"
                        >
                          {cert.label}
                        </Label>
                      </div>
                    ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Specializations (Select all that apply)</Label>
                <div className="space-y-2">
                  {FINANCIAL_SPECIALIZATIONS.map((spec) => (
                      <div
                        key={spec}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`spec-${spec}`}
                          checked={data.specializations?.includes(
                            spec,
                          )}
                          onCheckedChange={(checked) => {
                            const current =
                              data.specializations || [];
                            updateDataNew({
                              specializations: checked
                                ? [...current, spec]
                                : current.filter(
                                    (s) => s !== spec,
                                  ),
                            });
                          }}
                        />
                        <Label
                          htmlFor={`spec-${spec}`}
                          className="cursor-pointer"
                        >
                          {spec}
                        </Label>
                      </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="yearsAdvising">Years in Advisory</Label>
                <Select
                  value={data.yearsAdvising}
                  onValueChange={(value) => updateDataNew({ yearsAdvising: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select years of experience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-2">0-2 years</SelectItem>
                    <SelectItem value="3-5">3-5 years</SelectItem>
                    <SelectItem value="6-10">6-10 years</SelectItem>
                    <SelectItem value="10-15">10-15 years</SelectItem>
                    <SelectItem value="15+">15+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                  <Label>Preferred Client Risk Tolerance (Select all that apply)</Label>
                  <p className="text-sm text-muted-foreground">
                    What risk profiles do you prefer to work with?
                  </p>
                  <div className="space-y-2">
                    {RISK_TOLERANCE_LEVELS.map((level) => (
                      <div key={level.value} className="flex items-start space-x-2">
                        <Checkbox
                          id={`advisor-risk-${level.value}`}
                          checked={data.clientRiskTolerance?.includes(level.value)}
                          onCheckedChange={(checked) => {
                            const current = data.clientRiskTolerance || [];
                            updateDataNew({
                              clientRiskTolerance: checked
                                ? [...current, level.value]
                                : current.filter((r) => r !== level.value),
                            });
                          }}
                        />
                        <div className="flex flex-col">
                          <Label htmlFor={`advisor-risk-${level.value}`} className="cursor-pointer font-medium">
                            {level.label}
                          </Label>
                          <p className="text-sm text-muted-foreground">{level.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => goToNextStep(1)}>
                  Back
                </Button>
                <Button
                  onClick={() => goToNextStep(3)}
                  disabled={
                    !data.advisorCertifications?.length ||
                    !data.specializations?.length ||
                    !data.yearsAdvising
                  }
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
          <Footer />
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-2xl w-full">
            <LogoutButton />
            <CardHeader>
              <Progress value={progressPercentage} className="mb-4" />
              <CardTitle>Client Focus</CardTitle>
              <CardDescription>
                Tell us about your ideal clients and advisory approach
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Client Types (Select all that apply)</Label>
                <div className="space-y-2">
                  {[
                    "High Net Worth Individuals",
                    "Mass Affluent",
                    "Retirees",
                    "Young Professionals",
                    "Business Owners",
                    "Families",
                  ].map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`client-${type}`}
                        checked={data.clientTypes?.includes(type)}
                        onCheckedChange={(checked) => {
                          const current = data.clientTypes || [];
                          updateDataNew({
                            clientTypes: checked
                              ? [...current, type]
                              : current.filter((t) => t !== type),
                          });
                        }}
                      />
                      <Label htmlFor={`client-${type}`} className="cursor-pointer">
                        {type}
                      </Label>
                    </div>  
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="advisoryApproach">
                  Advisory Philosophy (Optional)
                </Label>
                <Textarea
                  id="advisoryApproach"
                  placeholder="Describe your approach to financial advisory..."
                  value={data.advisoryApproach || ""}
                  onChange={(e) =>
                    updateDataNew({ advisoryApproach: e.target.value })
                  }
                  rows={4}
                />
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button onClick={() => goToNextStep(4)} disabled={!data.clientTypes?.length}>
                  Continue                                    
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
          <Footer />
        </div>
      );
    }
  }

  // Fee Structure Step (for both Advisor and Analyst - Step 4)
  if (
    (data.role === "advisor" || data.role === "analyst") &&
    step === 4
  ) {
    // Initialize feeStructureOptions if not exists
    const feeOptions = data.feeStructureOptions || [];

    // Helper to add a quick fee structure preset from taxonomy
    const addQuickPreset = (feeTypeValue: string) => {
      let newFee: FeeStructureOption;

      if (data.role === "analyst") {
        const feeTypeDetails = ANALYST_FEE_TYPES.find(ft => ft.value === feeTypeValue);
        if (!feeTypeDetails) return;

        // Map taxonomy fee types to backend enum values
        const feeTypeMap: Record<string, FeeType> = {
          'per_request': 'FLAT_FEE',
          'subscription': 'SUBSCRIPTION',
          'tiered_subscription': 'SUBSCRIPTION',
          'retainer': 'RETAINER',
          'consulting_hourly': 'HOURLY',
          'project_based': 'FLAT_FEE',
          'hybrid': 'HYBRID',
          'data_feed': 'SUBSCRIPTION',
          'freemium': 'FREEMIUM',
        };

        const backendFeeType = feeTypeMap[feeTypeValue] || 'FLAT_FEE';

        // Create fee based on type with sensible defaults
        switch (feeTypeValue) {
          case 'consulting_hourly':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              hourlyRate: 300,
              minimumFee: 500,
              description: feeTypeDetails.description,
            };
            break;
          case 'per_request':
          case 'project_based':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 2500,
              description: feeTypeDetails.description,
            };
            break;
          case 'subscription':
          case 'tiered_subscription':
          case 'data_feed':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 500,
              billingFrequency: 'MONTHLY',
              description: feeTypeDetails.description,
            };
            break;
          case 'retainer':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 3000,
              billingFrequency: 'MONTHLY',
              description: feeTypeDetails.description,
            };
            break;
          case 'hybrid':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 500,
              hourlyRate: 200,
              billingFrequency: 'MONTHLY',
              description: feeTypeDetails.description,
            };
            break;
          case 'freemium':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 0,
              description: feeTypeDetails.description,
            };
            break;
          default:
            return;
        }
      } else {
        // Advisor role
        const feeTypeDetails = ADVISOR_FEE_TYPES.find(ft => ft.value === feeTypeValue);
        if (!feeTypeDetails) return;

        // Map taxonomy fee types to backend enum values
        const feeTypeMap: Record<string, FeeType> = {
          'aum': 'AUM_BASED',
          'flat_fee': 'FLAT_FEE',
          'hourly': 'HOURLY',
          'project_based': 'PROJECT_BASED',
          'commission': 'COMMISSION',
          'performance_based': 'PERFORMANCE_BASED',
          'hybrid': 'HYBRID',
          'subscription': 'SUBSCRIPTION',
        };

        const backendFeeType = feeTypeMap[feeTypeValue] || 'FLAT_FEE';

        // Create fee based on type with sensible defaults
        switch (feeTypeValue) {
          case 'aum':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              aumPercentage: 1.0,
              minimumFee: 2500,
              billingFrequency: 'QUARTERLY',
              description: feeTypeDetails.description,
            };
            break;
          case 'flat_fee':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 5000,
              billingFrequency: 'ANNUALLY',
              description: feeTypeDetails.description,
            };
            break;
          case 'hourly':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              hourlyRate: 250,
              minimumFee: 1000,
              description: feeTypeDetails.description,
            };
            break;
          case 'project_based':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 3000,
              description: feeTypeDetails.description,
            };
            break;
          case 'commission':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              aumPercentage: 3.0,
              description: feeTypeDetails.description,
            };
            break;
          case 'performance_based':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              performanceFeePercentage: 15.0,
              description: feeTypeDetails.description,
            };
            break;
          case 'hybrid':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              aumPercentage: 0.75,
              flatFeeAmount: 2000,
              billingFrequency: 'QUARTERLY',
              description: feeTypeDetails.description,
            };
            break;
          case 'subscription':
            newFee = {
              id: `fee-${Date.now()}`,
              feeType: backendFeeType,
              name: feeTypeDetails.label,
              flatFeeAmount: 200,
              billingFrequency: 'MONTHLY',
              description: feeTypeDetails.description,
            };
            break;
          default:
            return;
        }
      }

      updateDataNew({
        feeStructureOptions: [...feeOptions, newFee],
      });
    };

    const removeFeeOption = (id: string) => {
      updateDataNew({
        feeStructureOptions: feeOptions.filter(
          (fee) => fee.id !== id,
        ),
      });
    };

    const editFeeOption = (fee: FeeStructureOption) => {
      setManualFee(fee);
      setEditingFeeId(fee.id);
      setShowManualForm(true);
    };

    const saveManualFee = () => {
      if (!manualFee.name || !manualFee.feeType) return;

      const feeToSave: FeeStructureOption = {
        id: editingFeeId || `fee-${Date.now()}`,
        feeType: manualFee.feeType!,
        name: manualFee.name,
        hourlyRate: manualFee.hourlyRate,
        flatFeeAmount: manualFee.flatFeeAmount,
        aumPercentage: manualFee.aumPercentage,
        performanceFeePercentage:
          manualFee.performanceFeePercentage,
        minimumFee: manualFee.minimumFee,
        billingFrequency: manualFee.billingFrequency,
        description: manualFee.description,
      };

      let updatedFees;
      if (editingFeeId) {
        updatedFees = feeOptions.map((f) =>
          f.id === editingFeeId ? feeToSave : f,
        );
      } else {
        updatedFees = [...feeOptions, feeToSave];
      }

      updateDataNew({ feeStructureOptions: updatedFees });

      // Reset form
      setManualFee({ feeType: undefined, name: "" });
      setEditingFeeId(null);
      setShowManualForm(false);
    };

    const cancelManualForm = () => {
      setManualFee({ feeType: undefined, name: "" });
      setEditingFeeId(null);
      setShowManualForm(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
            <LogoutButton />
            <CardHeader>
              <Progress
                value={progressPercentage}
                className="mb-4"
              />
              <div className="flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-blue-600" />
                <CardTitle>Fee Structure & Pricing</CardTitle>
              </div>
              <CardDescription>
                {data.role === "advisor"
                  ? "Configure your advisory fees. You can add multiple pricing options to offer flexibility to clients."
                  : "Set up your consulting rates and service pricing. Add multiple options for different types of projects and engagements."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quick Presets - Dynamically generated from taxonomy */}
              <div className="space-y-3">
                <Label>
                  Quick Start - Add Common Fee Structures
                </Label>
                <div className="text-xs text-muted-foreground mb-2">
                  Click any fee type below to add it to your profile. You can customize the details after adding.
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-2">
                  {data.role === "analyst" 
                    ? ANALYST_FEE_TYPES.map((feeType) => (
                        <Button
                          key={feeType.value}
                          type="button"
                          variant="outline"
                          onClick={() => addQuickPreset(feeType.value)}
                          className="h-auto py-3 flex flex-col items-start gap-1 text-left"
                          title={feeType.description}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <DollarSign className="h-4 w-4 flex-shrink-0" />
                            <div className="text-xs font-medium truncate">
                              {feeType.label}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground w-full truncate">
                            {feeType.typicalRange}
                          </div>
                        </Button>
                      ))
                    : ADVISOR_FEE_TYPES.map((feeType) => (
                        <Button
                          key={feeType.value}
                          type="button"
                          variant="outline"
                          onClick={() => addQuickPreset(feeType.value)}
                          className="h-auto py-3 flex flex-col items-start gap-1 text-left"
                          title={feeType.description}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <DollarSign className="h-4 w-4 flex-shrink-0" />
                            <div className="text-xs font-medium truncate">
                              {feeType.label}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground w-full truncate">
                            {feeType.typicalRange}
                          </div>
                        </Button>
                      ))
                  }
                </div>
              </div>

              {/* Manual Fee Entry Form - Or button */}
              {!showManualForm && (
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="h-px flex-1 bg-border"></div>
                    <span>or</span>
                    <div className="h-px flex-1 bg-border"></div>
                  </div>
                </div>
              )}

              {!showManualForm && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowManualForm(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Custom Fee Structure
                </Button>
              )}

              {/* Manual Fee Form */}
              {showManualForm && (
                <ManualFeeForm
                  role={data.role as "advisor" | "analyst"}
                  initialFee={
                    editingFeeId
                      ? feeOptions.find(
                          (f) => f.id === editingFeeId,
                        )
                      : undefined
                  }
                  onSave={(fee) => {
                    let updatedFees;
                    if (editingFeeId) {
                      updatedFees = feeOptions.map((f) =>
                        f.id === editingFeeId ? fee : f,
                      );
                    } else {
                      updatedFees = [...feeOptions, fee];
                    }
                    updateDataNew({
                      feeStructureOptions: updatedFees,
                    });
                    setShowManualForm(false);
                    setEditingFeeId(null);
                  }}
                  onCancel={cancelManualForm}
                />
              )}

              {/* Added Fee Structures */}
              {feeOptions.length > 0 && (
                <div className="space-y-3">
                  <Label>
                    Your Fee Structures ({feeOptions.length})
                  </Label>
                  <div className="space-y-2">
                    {feeOptions.map((fee) => (
                      <div
                        key={fee.id}
                        className="p-4 border rounded-lg bg-card flex items-start justify-between hover:border-primary transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">
                              {fee.name}
                            </span>
                            <Badge
                              variant="secondary"
                              className="text-xs"
                            >
                              {fee.feeType.replace("_", " ")}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground space-y-0.5">
                            {fee.hourlyRate && (
                              <div>
                                Hourly: ${fee.hourlyRate}/hr
                              </div>
                            )}
                            {fee.flatFeeAmount && (
                              <div>
                                Fee: ${fee.flatFeeAmount}
                              </div>
                            )}
                            {fee.aumPercentage && (
                              <div>
                                {data.role === "advisor"
                                  ? "AUM"
                                  : "Retainer"}
                                :{" "}
                                {fee.aumPercentage > 100
                                  ? `$${fee.aumPercentage}`
                                  : `${fee.aumPercentage}%`}
                              </div>
                            )}
                            {fee.minimumFee && (
                              <div>
                                Minimum: ${fee.minimumFee}
                              </div>
                            )}
                            {fee.billingFrequency && (
                              <div>
                                Billing: {fee.billingFrequency}
                              </div>
                            )}
                            {fee.description && (
                              <div className="text-xs italic mt-1">
                                {fee.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => editFeeOption(fee)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              removeFeeOption(fee.id)
                            }
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consultation Settings */}
              <div className="space-y-4 p-4 bg-card border rounded-lg">
                <Label className="text-base">
                  Additional Settings
                </Label>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>
                      Offer Free Initial Consultation
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {data.role === "advisor"
                        ? "Attract clients with a complimentary first meeting"
                        : "Offer a free discovery call to discuss project scope and fit"}
                    </p>
                  </div>
                  <Switch
                    checked={data.freeConsultation || false}
                    onCheckedChange={(checked) =>
                      updateDataNew({ freeConsultation: checked })
                    }
                  />
                </div>

                {data.freeConsultation && (
                  <div className="space-y-2">
                    <Label htmlFor="consultationDuration">
                      Consultation Duration (minutes)
                    </Label>
                    <Input
                      id="consultationDuration"
                      type="number"
                      placeholder="e.g., 30"
                      value={data.consultationDuration || ""}
                      onChange={(e) =>
                        updateDataNew({
                          consultationDuration:
                            parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                )}

                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>
                        Currently Accepting New Clients
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {data.role === "advisor"
                          ? "Make your profile visible to potential clients seeking advisors"
                          : "Allow potential clients to view your services and send project inquiries"}
                      </p>
                    </div>
                    <Switch
                      checked={
                        data.acceptingNewClients || false
                      }
                      onCheckedChange={(checked) =>
                        updateDataNew({
                          acceptingNewClients: checked,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {feeOptions.length === 0 && (
                <div className="p-6 border-2 border-dashed rounded-lg text-center text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {data.role === "analyst"
                      ? "Add at least one fee structure to continue. Use the quick presets above or you can customize these later."
                      : "Add at least one fee structure to continue. Choose from the presets above."}
                  </p>
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                >
                  Back
                </Button>
                <Button
                  onClick={() => goToNextStep(5)}
                  disabled={feeOptions.length === 0}
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Final Step: Completion
  const completionStep = data.role?.toLowerCase() === "investor" ? 5 : 5;
  if (step === completionStep) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
          <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-2xl w-full">
          <LogoutButton />
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-3xl">Welcome Aboard!</CardTitle>
            <CardDescription>
              Your profile has been created successfully
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <h3 className="text-sm">Profile Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name:</span>
                  <span>{data.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span>{data.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role:</span>
                  <span className="capitalize">{data.role}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm">What's Next?</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span>Explore your personalized dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span>Connect with other professionals</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span>Access AI-powered insights and recommendations</span>
                </li>
              </ul>
            </div>

            <Button onClick={handleComplete} className="w-full" size="lg">
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return null;
}